<!-- src/apps/secret/conceal/HomepageContent.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import OIcon from '@/shared/components/icons/OIcon.vue';
  import SecretForm from '@/apps/secret/components/form/SecretForm.vue';
  import RecentSecretsTable from '@/apps/secret/components/RecentSecretsTable.vue';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import { useProductIdentity } from '@/shared/stores/identityStore';
  import { useLocalReceiptStore } from '@/shared/stores/localReceiptStore';
  import { storeToRefs } from 'pinia';

  const { t } = useI18n();

  const bootstrapStore = useBootstrapStore();
  const { authenticated, ui } = storeToRefs(bootstrapStore);

  // Resolve the active brand color so the CTA tracks the same source as the
  // logo (identityStore.primaryColor → --color-brand-500). Without this the
  // button falls back to SecretForm's neutral-blue default, so a branded
  // install shows a brand-colored logo above a neutral-blue button.
  const identityStore = useProductIdentity();
  const { primaryColor } = storeToRefs(identityStore);

  const localReceiptStore = useLocalReceiptStore();

  // Closing-CTA action: scroll the secret composer into view and drop focus
  // into its textarea for keyboard users. Honors prefers-reduced-motion.
  const scrollToComposer = () => {
    const composer = document.getElementById('composer');
    if (!composer) return;
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    composer.scrollIntoView({
      behavior: prefersReduced ? 'auto' : 'smooth',
      block: 'start',
    });
    const textarea = composer.querySelector('textarea');
    (textarea as HTMLTextAreaElement | null)?.focus();
  };
</script>

<template>
  <div class="min-w-[320px]">
    <!-- ============================================================ -->
    <!-- 1. HERO — headline + the live secret composer               -->
    <!-- ============================================================ -->
    <section
      aria-labelledby="hero-heading"
      class="w-full pb-12 pt-2 sm:pb-14 sm:pt-3">
      <div class="mx-auto max-w-5xl px-4 text-center sm:px-6">
        <!-- Eyebrow label -->
        <p
          class="mb-4 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-brand-600 dark:text-brand-400">
          <OIcon
            collection="heroicons"
            name="fire"
            class="size-3.5 text-brandcomp-500"
            aria-hidden="true" />
          {{ t('web.landing.eyebrow') }}
        </p>

        <!-- Headline (reuses existing i18n tagline, scaled up) -->
        <h1
          id="hero-heading"
          class="mx-auto max-w-3xl text-balance text-2xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
          {{ t('web.COMMON.tagline') }}
        </h1>

        <!-- Subcopy: the action prompt — points down at the composer. -->
        <p class="mx-auto mt-2 max-w-xl text-base text-gray-600 dark:text-gray-300 sm:mt-3 sm:text-lg">
          {{ t('web.homepage.tagline1') }}
        </p>

        <!-- Composer: the live product action, framed like a console -->
        <div
          id="composer"
          class="mt-3 scroll-mt-24 text-left sm:mt-6">
          <div class="mx-auto max-w-2xl">
            <!-- Terminal-style caption bar -->
            <div class="mb-3 flex items-center gap-2 px-1">
              <span
                class="flex items-center gap-1.5"
                aria-hidden="true">
                <span class="size-2.5 rounded-full bg-brandcomp-500"></span>
                <span class="size-2.5 rounded-full bg-brand-400"></span>
                <span class="size-2.5 rounded-full bg-gray-300 dark:bg-gray-600"></span>
              </span>
              <span class="font-mono text-xs text-gray-500 dark:text-gray-400">
                {{ t('web.landing.composer_caption') }}
              </span>
              <!-- One-time signal badge (orange = signal only) -->
              <span
                class="ml-auto inline-flex items-center gap-1 rounded-md border border-brandcomp-500/40 bg-brandcomp-50 px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wide text-brandcomp-700 dark:bg-brandcomp-900/30 dark:text-brandcomp-300">
                <OIcon
                  collection="heroicons"
                  name="fire"
                  class="size-3"
                  aria-hidden="true" />
                {{ t('web.landing.composer_badge') }}
              </span>
            </div>

            <!--
              The public homepage always navigates to the /receipt link screen
              after creating (OTS default). Workspace mode — "stay on page after
              create" — is a dashboard-only power feature, so it is deliberately
              NOT wired here (matches BrandedHomepage). Otherwise a persisted
              onetimeWorkspaceMode=true from a prior session would silently
              suppress navigation and strand anonymous users on the form.
            -->
            <SecretForm
              v-if="ui?.enabled !== false"
              :with-recipient="false"
              :with-asterisk="true"
              :with-generate="true"
              :primary-color="primaryColor" />

            <!-- Supporting hint below the form (the former subcopy line). -->
            <p class="mt-3 px-1 font-mono text-xs text-gray-500 dark:text-gray-400">
              {{ t('web.homepage.tagline2') }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Recent receipts — kept below the composer for authed/returning users -->
    <div
      v-if="authenticated || localReceiptStore.hasReceipts"
      class="w-full">
      <div class="mx-auto max-w-5xl px-4 sm:px-6">
        <div class="mx-auto max-w-2xl">
          <!--
            Hide the workspace-mode toggle on the public homepage: workspace
            mode has no effect here (the composer always navigates), so exposing
            the checkbox would be a dead control that silently persists a
            preference only the dashboard honors.
          -->
          <RecentSecretsTable :show-workspace-mode-toggle="false" />
        </div>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- 2. CAPABILITY STRIP — dark console band (both themes)        -->
    <!-- ============================================================ -->
    <section
      aria-labelledby="capabilities-heading"
      class="w-full">
      <div class="mx-auto max-w-5xl px-4 sm:px-6">
        <h2
          id="capabilities-heading"
          class="sr-only">
          {{ t('web.landing.capabilities_heading') }}
        </h2>
        <ul
          class="flex flex-wrap items-center gap-x-6 gap-y-4 rounded-lg border border-gray-800 bg-gray-950 px-5 py-6 sm:px-8">
          <!-- One-time reveal (burn/one-time → orange signal) -->
          <li class="flex items-center gap-2.5">
            <OIcon
              collection="heroicons"
              name="fire"
              class="size-5 shrink-0 text-brandcomp-500"
              aria-hidden="true" />
            <span class="text-sm font-medium text-gray-100">{{
              t('web.landing.capability_one_time_reveal')
            }}</span>
          </li>
          <!-- Passphrase lock -->
          <li class="flex items-center gap-2.5">
            <OIcon
              collection="heroicons"
              name="lock-closed"
              class="size-5 shrink-0 text-brand-400"
              aria-hidden="true" />
            <span class="text-sm font-medium text-gray-100">{{
              t('web.landing.capability_passphrase_lock')
            }}</span>
          </li>
          <!-- Expiration control -->
          <li class="flex items-center gap-2.5">
            <OIcon
              collection="heroicons"
              name="clock"
              class="size-5 shrink-0 text-brand-400"
              aria-hidden="true" />
            <span class="text-sm font-medium text-gray-100">{{
              t('web.landing.capability_expiration_control')
            }}</span>
          </li>
          <!-- Audit-ready links -->
          <li class="flex items-center gap-2.5">
            <OIcon
              collection="heroicons"
              name="link-solid"
              class="size-5 shrink-0 text-brand-400"
              aria-hidden="true" />
            <span class="text-sm font-medium text-gray-100">{{
              t('web.landing.capability_audit_ready_links')
            }}</span>
          </li>
          <!-- Private by default -->
          <li class="flex items-center gap-2.5">
            <OIcon
              collection="heroicons"
              name="finger-print"
              class="size-5 shrink-0 text-brand-400"
              aria-hidden="true" />
            <span class="text-sm font-medium text-gray-100">{{
              t('web.landing.capability_private_by_default')
            }}</span>
          </li>
        </ul>
      </div>
    </section>

    <!-- ============================================================ -->
    <!-- 3. LIFECYCLE — 3 numbered steps (03 = burn, orange signal)  -->
    <!-- ============================================================ -->
    <section
      aria-labelledby="lifecycle-heading"
      class="w-full py-14 sm:py-20">
      <div class="mx-auto max-w-5xl px-4 sm:px-6">
        <h2
          id="lifecycle-heading"
          class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-3xl">
          {{ t('web.landing.lifecycle_heading') }}
        </h2>
        <p class="mt-3 max-w-2xl text-base text-gray-600 dark:text-gray-300">
          {{ t('web.landing.lifecycle_subcopy') }}
        </p>

        <ol class="mt-8 grid gap-5 md:grid-cols-3">
          <!-- 01 Compose & encrypt -->
          <li
            class="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-slate-900/40">
            <div class="flex items-center justify-between">
              <span class="font-mono text-sm font-semibold text-brand-600 dark:text-brand-400">
                01
              </span>
              <OIcon
                collection="heroicons"
                name="document-text"
                class="size-6 text-brand-500 dark:text-brand-400"
                aria-hidden="true" />
            </div>
            <h3 class="mt-4 text-base font-semibold text-gray-900 dark:text-white">
              {{ t('web.landing.lifecycle_step1_title') }}
            </h3>
            <p class="mt-1.5 text-sm text-gray-600 dark:text-gray-400">
              {{ t('web.landing.lifecycle_step1_body') }}
            </p>
          </li>

          <!-- 02 Share one link -->
          <li
            class="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-slate-900/40">
            <div class="flex items-center justify-between">
              <span class="font-mono text-sm font-semibold text-brand-600 dark:text-brand-400">
                02
              </span>
              <OIcon
                collection="heroicons"
                name="link-solid"
                class="size-6 text-brand-500 dark:text-brand-400"
                aria-hidden="true" />
            </div>
            <h3 class="mt-4 text-base font-semibold text-gray-900 dark:text-white">
              {{ t('web.landing.lifecycle_step2_title') }}
            </h3>
            <p class="mt-1.5 text-sm text-gray-600 dark:text-gray-400">
              {{ t('web.landing.lifecycle_step2_body') }}
            </p>
          </li>

          <!-- 03 Destroyed on open (burn step → orange signal accent) -->
          <li
            class="rounded-lg border border-brandcomp-500/30 bg-white p-6 dark:border-brandcomp-500/30 dark:bg-slate-900/40">
            <div class="flex items-center justify-between">
              <span
                class="font-mono text-sm font-semibold text-brandcomp-600 dark:text-brandcomp-500">
                03
              </span>
              <OIcon
                collection="heroicons"
                name="fire"
                class="size-6 text-brandcomp-500"
                aria-hidden="true" />
            </div>
            <h3 class="mt-4 text-base font-semibold text-gray-900 dark:text-white">
              {{ t('web.landing.lifecycle_step3_title') }}
            </h3>
            <p class="mt-1.5 text-sm text-gray-600 dark:text-gray-400">
              {{ t('web.landing.lifecycle_step3_body') }}
            </p>
          </li>
        </ol>
      </div>
    </section>

    <!-- ============================================================ -->
    <!-- 4. SECURITY / TRUST — mono spec sheet                        -->
    <!-- ============================================================ -->
    <section
      aria-labelledby="security-heading"
      class="w-full py-14 sm:py-20">
      <div
        class="mx-auto grid max-w-5xl gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:items-center">
        <div>
          <h2
            id="security-heading"
            class="text-balance text-2xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-3xl">
            {{ t('web.landing.security_heading') }}
          </h2>
          <p class="mt-4 max-w-md text-base text-gray-600 dark:text-gray-300">
            {{ t('web.landing.security_subcopy') }}
          </p>
        </div>

        <!-- Spec sheet -->
        <dl
          class="divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200 bg-white font-mono text-sm dark:divide-gray-800 dark:border-gray-800 dark:bg-slate-900/40">
          <div class="flex items-center justify-between gap-4 px-4 py-3">
            <dt class="text-gray-500 dark:text-gray-400">
              {{ t('web.landing.spec_encryption_label') }}
            </dt>
            <dd class="text-gray-900 dark:text-white">
              {{ t('web.landing.spec_encryption_value') }}
            </dd>
          </div>
          <div class="flex items-center justify-between gap-4 px-4 py-3">
            <dt class="text-gray-500 dark:text-gray-400">
              {{ t('web.landing.spec_reads_label') }}
            </dt>
            <dd class="font-semibold text-brandcomp-600 dark:text-brandcomp-500">
              {{ t('web.landing.spec_reads_value') }}
            </dd>
          </div>
          <div class="flex items-center justify-between gap-4 px-4 py-3">
            <dt class="text-gray-500 dark:text-gray-400">
              {{ t('web.landing.spec_retrieval_label') }}
            </dt>
            <dd class="text-brandcomp-600 dark:text-brandcomp-500">
              {{ t('web.landing.spec_retrieval_value') }}
            </dd>
          </div>
          <div class="flex items-center justify-between gap-4 px-4 py-3">
            <dt class="text-gray-500 dark:text-gray-400">
              {{ t('web.landing.spec_ttl_label') }}
            </dt>
            <dd class="text-gray-900 dark:text-white">
              {{ t('web.landing.spec_ttl_value') }}
            </dd>
          </div>
          <div class="flex items-center justify-between gap-4 px-4 py-3">
            <dt class="text-gray-500 dark:text-gray-400">
              {{ t('web.landing.spec_logs_label') }}
            </dt>
            <dd class="text-gray-900 dark:text-white">
              {{ t('web.landing.spec_logs_value') }}
            </dd>
          </div>
          <div class="flex items-center justify-between gap-4 px-4 py-3">
            <dt class="text-gray-500 dark:text-gray-400">
              {{ t('web.landing.spec_license_label') }}
            </dt>
            <dd class="inline-flex items-center gap-1.5 text-gray-900 dark:text-white">
              <OIcon
                collection="heroicons"
                name="check-circle"
                class="size-4 text-brand-500 dark:text-brand-400"
                aria-hidden="true" />
              {{ t('web.landing.spec_license_value') }}
            </dd>
          </div>
        </dl>
      </div>
    </section>

    <!-- ============================================================ -->
    <!-- 5. CLOSING CTA band                                          -->
    <!-- ============================================================ -->
    <section
      aria-labelledby="cta-heading"
      class="w-full pb-16">
      <div class="mx-auto max-w-5xl px-4 sm:px-6">
        <div
          class="rounded-lg border border-brand-200 bg-brand-50 px-6 py-10 text-center dark:border-brand-900/60 dark:bg-brand-950/30 sm:px-10 sm:py-14">
          <h2
            id="cta-heading"
            class="text-balance text-2xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-3xl">
            {{ t('web.landing.cta_heading') }}
          </h2>
          <p class="mx-auto mt-3 max-w-lg text-base text-gray-600 dark:text-gray-300">
            {{ t('web.landing.cta_subcopy') }}
          </p>
          <button
            type="button"
            @click="scrollToComposer"
            class="mt-6 inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-brand-600 px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-50 motion-reduce:transition-none dark:focus-visible:ring-offset-brand-950">
            <OIcon
              collection="heroicons"
              name="arrow-down"
              class="size-5"
              aria-hidden="true" />
            {{ t('web.landing.cta_button') }}
          </button>
        </div>
      </div>
    </section>
  </div>
</template>
